import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Spin,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  useAcceptAllPolicies,
  useCurrentPolicy,
} from '@/entities/policy';
import type {
  DocumentType,
  PendingPolicyAcceptance,
} from '@/entities/policy';
import { useMyProfile } from '@/entities/user';
import { useMediaQuery } from '@/shared/hooks';
import { usePolicyAcceptanceModal } from '../model/usePolicyAcceptanceModal';
import { useScrollDetection } from '../model/useScrollDetection';
import { PolicyContent } from './PolicyContent';

const { Title, Text } = Typography;

export function PolicyAcceptanceModal() {
  const { t } = useTranslation();
  const {
    isOpen,
    close,
    pending: interceptedPending,
  } = usePolicyAcceptanceModal();
  const {
    hasScrolledToBottom,
    scrollRef,
    reset: resetScroll,
  } = useScrollDetection();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxUnlockedIndex, setMaxUnlockedIndex] = useState(0);
  const [hasAgreed, setHasAgreed] = useState(false);
  const previousIsOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !previousIsOpen.current) {
      setCurrentIndex(0);
      setMaxUnlockedIndex(0);
      setHasAgreed(false);
      resetScroll();
    }
    previousIsOpen.current = isOpen;
  }, [isOpen, resetScroll]);

  const isMobile = useMediaQuery('(max-width: 576px)');
  const isTablet = useMediaQuery('(max-width: 768px)');
  const modalWidth = isMobile
    ? 'calc(100vw - 32px)'
    : isTablet
      ? '96vw'
      : 1080;

  const { data: profileData, refetch: refetchProfile } = useMyProfile();
  const privacyPolicyQuery = useCurrentPolicy('PRIVACY_POLICY', isOpen);
  const termsQuery = useCurrentPolicy('TERMS_AND_CONDITIONS', isOpen);
  const pending = useMemo(() => {
    const documents = new Map<DocumentType, PendingPolicyAcceptance>();
    const profilePending =
      (profileData?.data?.pendingPolicyAcceptances ?? []) as
        PendingPolicyAcceptance[];
    [...profilePending, ...interceptedPending].forEach((item) => {
      documents.set(item.documentType, item);
    });

    [privacyPolicyQuery.data?.data, termsQuery.data?.data].forEach((policy) => {
      if (policy?.requiresAcceptance && policy.hasAccepted === false) {
        documents.set(policy.documentType, {
          policyId: policy.id,
          documentType: policy.documentType,
          versionNumber: policy.versionNumber,
          title: policy.title,
        });
      }
    });

    return Array.from(documents.values()).sort((left) =>
      left.documentType === 'PRIVACY_POLICY' ? -1 : 1
    );
  }, [
    interceptedPending,
    privacyPolicyQuery.data?.data,
    profileData?.data?.pendingPolicyAcceptances,
    termsQuery.data?.data,
  ]);
  const safeCurrentIndex = Math.min(
    currentIndex,
    Math.max(pending.length - 1, 0),
  );
  const currentDocumentType: DocumentType =
    pending[safeCurrentIndex]?.documentType ?? 'PRIVACY_POLICY';
  const currentPolicyQuery =
    currentDocumentType === 'TERMS_AND_CONDITIONS'
      ? termsQuery
      : privacyPolicyQuery;
  const policyData = currentPolicyQuery.data;
  const policyError = currentPolicyQuery.error;
  const isPolicyLoading =
    privacyPolicyQuery.isLoading || termsQuery.isLoading;
  const {
    mutate: acceptAllPolicies,
    isPending: isAccepting,
  } = useAcceptAllPolicies();

  const isNotPublished =
    (policyError as { response?: { status?: number } } | null)?.response
      ?.status === 404;
  const isLastDocument =
    pending.length <= 1 || safeCurrentIndex === pending.length - 1;
  const hasMultipleDocuments = pending.length > 1;

  useEffect(() => {
    resetScroll();
  }, [safeCurrentIndex, resetScroll]);

  const handleAccept = () => {
    acceptAllPolicies(undefined, {
      onSuccess: async () => {
        const result = await refetchProfile();
        const remaining =
          (result.data?.data?.pendingPolicyAcceptances ?? []) as
            PendingPolicyAcceptance[];
        if (remaining.length === 0) close();
      },
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error('Policy acceptance failed:', error);
      },
    });
  };

  const policy = policyData?.data;
  const formattedUpdatedAt = useMemo(() => {
    const updatedAt = policy?.publishedAt
      ? new Date(policy.publishedAt)
      : policy?.updatedAt
        ? new Date(policy.updatedAt)
        : null;
    return updatedAt && !Number.isNaN(updatedAt.getTime())
      ? updatedAt.toLocaleDateString()
      : '\u2014';
  }, [policy?.publishedAt, policy?.updatedAt]);

  if (!isOpen) return null;

  const modalTitle = t(
    'legalSupport.acceptanceModal.legalAndPrivacy',
    'Legal & Privacy',
  );

  const footer = isLastDocument
    ? [
        ...(hasMultipleDocuments
          ? [
              <Button
                key="previous"
                disabled={isAccepting}
                onClick={() => setCurrentIndex((index) => Math.max(index - 1, 0))}
              >
                {t("actions.previous", 'Previous')}
              </Button>,
            ]
          : []),
        <Button
          key="accept"
          type="primary"
          loading={isAccepting}
          disabled={!hasScrolledToBottom || !hasAgreed || isPolicyLoading}
          onClick={handleAccept}
        >
          {t('legalSupport.acceptanceModal.acceptAll', 'Accept')}
        </Button>,
      ]
    : [
        <Button
          key="next"
          type="primary"
          disabled={!hasScrolledToBottom || isPolicyLoading}
          onClick={() => {
            const nextIndex = safeCurrentIndex + 1;
            setMaxUnlockedIndex((index) => Math.max(index, nextIndex));
            setCurrentIndex(nextIndex);
          }}
        >
          {t("actions.next", 'Next')}
        </Button>,
      ];

  return (
    <Modal
      open={isOpen}
      title={
        <Title level={2} style={{ margin: 0, fontSize: 24, lineHeight: 1.25 }}>
          {modalTitle}
        </Title>
      }
      closable={false}
      mask
      maskClosable={false}
      keyboard={false}
      footer={footer}
      width={modalWidth}
      centered
      styles={{
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(2px)',
        },
        content: {
          maxHeight: 'calc(100vh - 32px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          minHeight: 0,
          overflowY: 'auto',
        },
      }}
    >
      {isPolicyLoading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      )}

      {policyError && !isNotPublished && (
        <Alert
          type="error"
          message={t(
            'legalSupport.acceptanceModal.loadError',
            'Failed to load policy',
          )}
          description={t(
            'legalSupport.acceptanceModal.loadErrorDesc',
            'Please refresh the page to try again',
          )}
        />
      )}

      {isNotPublished && (
        <Alert
          type="info"
          message={t(
            'legalSupport.acceptanceModal.noPolicy',
            'No legal document has been published yet.',
          )}
        />
      )}

      {policy && !isPolicyLoading && (
        <div className="min-h-0">
          {hasMultipleDocuments && (
            <nav
              aria-label={t(
                'legalSupport.acceptanceModal.documents',
                'Updated legal documents',
              )}
              role="tablist"
              className="mb-4 flex w-full gap-1 border-b border-gray-200"
            >
              {pending.map((item, index) => {
                const isActive = index === safeCurrentIndex;
                const isLocked = index > maxUnlockedIndex;
                return (
                  <button
                    key={item.documentType}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    disabled={isLocked}
                    aria-disabled={isLocked}
                    onClick={() => {
                      if (!isLocked) setCurrentIndex(index);
                    }}
                    className={[
                      '-mb-px border-x-0 border-t-0 border-b-2 bg-transparent px-4 py-2.5 text-left text-sm transition-colors',
                      isActive
                        ? 'border-emerald-500 font-semibold text-emerald-700'
                        : isLocked
                          ? 'cursor-not-allowed border-transparent text-tertiary opacity-60'
                        : 'border-transparent text-tertiary hover:border-gray-300 hover:text-primary',
                    ].join(' ')}
                  >
                    {item.title}
                  </button>
                );
              })}
            </nav>
          )}

          <motion.div
            key={policy.id}
            className="min-w-0 flex-1"
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
            <Title level={4} style={{ margin: 0, fontSize: 18 }}>
              {policy.title}
            </Title>
            <div className="flex items-center gap-2 text-dense text-tertiary">
              <Text strong style={{ fontSize: 13 }}>
                V{policy.versionNumber}
              </Text>
              <span aria-hidden="true">{'\u00B7'}</span>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t(
                  'legalSupport.acceptanceModal.lastUpdated',
                  'Last Updated',
                )}
                : {formattedUpdatedAt}
              </Text>
            </div>
          </div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {hasMultipleDocuments
              ? t(
                  'legalSupport.acceptanceModal.multipleDescription',
                  'We have updated our Privacy Policy and Terms & Conditions. Please review both documents to continue using the platform.',
                )
              : t(
                  'legalSupport.acceptanceModal.description',
                  'We have updated this legal document. Please review and accept to continue using the platform.',
                )}
          </Text>

          <div style={{ marginTop: 14 }}>
            <PolicyContent
              ref={scrollRef}
              html={policy.content}
              compact
            />
          </div>

          {!hasScrolledToBottom && (
            <Alert
              type="info"
              message={t(
                'legalSupport.acceptanceModal.scrollMessage',
                'Please scroll to the bottom to continue',
              )}
              style={{ marginTop: 14, fontSize: 13 }}
            />
          )}

          {isLastDocument && hasScrolledToBottom && (
            <label
              style={{
                cursor: 'pointer',
                display: 'inline-block',
                lineHeight: '20px',
                marginTop: 18,
                minHeight: 20,
                paddingLeft: 30,
                position: 'relative',
                whiteSpace: 'nowrap',
                width: 'fit-content',
              }}
            >
              <input
                type="checkbox"
                checked={hasAgreed}
                onChange={(event) => setHasAgreed(event.currentTarget.checked)}
                style={{
                  accentColor: 'var(--brand-primary)',
                  display: 'block',
                  height: 20,
                  left: 0,
                  margin: 0,
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 20,
                }}
              />
              <span
                style={{
                  display: 'block',
                  fontSize: 13,
                  lineHeight: '20px',
                }}
              >
                {t(
                  'legalSupport.acceptanceModal.agreement',
                  'I have read and agree to the policies in both documents.',
                )}
              </span>
            </label>
          )}
          </motion.div>
        </div>
      )}
    </Modal>
  );
}
